import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Shield, Clock, X, ArrowLeft } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { createCheckoutSession } from '../utils/stripe';
import AuthModal from '../components/AuthModal';

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const [isYearly, setIsYearly] = useState(searchParams.get('interval') === 'yearly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleCheckout = async (productId: string) => {
    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await createCheckoutSession(productId as keyof typeof import('../stripe-config').PRODUCTS, isYearly);
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout process');
      setLoading(false);
    }
  };

  const features = [
    'Access to AI Learning Hub',
    'Unlimited AI Tutoring Sessions',
    'Community Support',
    'Priority Support',
    'Advanced Features',
    'Free AI Side Hustle Course'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get unlimited access to both our AI Learning Hub and AI Tutor Hub with a single subscription.
          </p>
        </div>

        <div className="flex justify-center mb-12">
          <div className="bg-white rounded-lg p-1 inline-flex">
            <button
              onClick={() => setIsYearly(false)}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                !isYearly
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                isYearly
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Yearly (Save 25%)
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Standard Plan */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Standard Plan</h3>
                <p className="text-gray-600 mt-2">Perfect for getting started</p>
              </div>
              <Shield className="w-8 h-8 text-indigo-600" />
            </div>

            <div className="mb-6">
              <p className="text-4xl font-bold text-gray-900">
                ${isYearly ? '441' : '49'}
                <span className="text-lg text-gray-600">
                  /{isYearly ? 'year' : 'month'}
                </span>
              </p>
              {isYearly && (
                <p className="text-indigo-600 font-medium mt-1">Save 25% yearly</p>
              )}
            </div>

            <ul className="space-y-4 mb-8">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleCheckout('AI_LEARNING_HUB')}
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Get Started'}
            </button>
          </div>

          {/* Pro Plan */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-indigo-600 relative">
            <div className="absolute -top-4 right-8 bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-medium">
              Most Popular
            </div>

            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Pro Plan</h3>
                <p className="text-gray-600 mt-2">For serious learners</p>
              </div>
              <Clock className="w-8 h-8 text-indigo-600" />
            </div>

            <div className="mb-6">
              <p className="text-4xl font-bold text-gray-900">
                ${isYearly ? '891' : '99'}
                <span className="text-lg text-gray-600">
                  /{isYearly ? 'year' : 'month'}
                </span>
              </p>
              {isYearly && (
                <p className="text-indigo-600 font-medium mt-1">Save 25% yearly</p>
              )}
            </div>

            <ul className="space-y-4 mb-8">
              {[...features, 'Premium Course Access', '24/7 Priority Support', 'Custom Learning Path'].map((feature, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleCheckout('AI_TUTOR_HUB')}
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Get Pro Access'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-8 max-w-md mx-auto bg-red-50 text-red-600 p-4 rounded-lg flex items-start">
            <X className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="mt-12 text-center text-gray-600">
          <p>All plans include a 5-day free trial. Cancel anytime.</p>
          <p className="mt-2">
            Questions? <button onClick={() => navigate('/contact')} className="text-indigo-600 hover:text-indigo-800">Contact our support team</button>
          </p>
        </div>
      </div>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />
    </div>
  );
}